import {
  ArrowRightLeft,
  AtSign,
  Bell,
  BellRing,
  BriefcaseBusiness,
  CircleCheck,
  ClipboardCheck,
  MessageSquare,
  MessageSquareHeart,
  Stamp,
  UserPlus,
  UserRoundCheck,
  type LucideIcon,
} from "lucide-react";

import { RichSentence } from "@/features/collaboration/components/RichSentence";
import { TimeAgo } from "@/features/collaboration/components/TimeAgo";
import { textDirection } from "@/features/collaboration/lib/direction";
import {
  notificationDetail,
  notificationExcerpt,
  notificationSentence,
  type Notification,
} from "@/features/notifications/lib/sentences";
import { useLocale, useT } from "@/shared/i18n/useT";
import { cn } from "@/shared/lib/cn";
import { Avatar } from "@/shared/ui/Avatar";

/* A small badge on the avatar says what kind of news it is before a word is read. */
const KIND_ICON: Partial<Record<Notification["kind"], LucideIcon>> = {
  assigned: UserRoundCheck,
  mentioned: AtSign,
  commented: MessageSquare,
  status_changed: ArrowRightLeft,
  member_added: UserPlus,
  request_pending: Stamp,
  request_decided: CircleCheck,
  hr_alert: BellRing,
  review_requested: ClipboardCheck,
  review_published: ClipboardCheck,
  feedback_received: MessageSquareHeart,
  feedback_requested: MessageSquareHeart,
  application_received: BriefcaseBusiness,
  interview_scheduled: BriefcaseBusiness,
  offer_answered: BriefcaseBusiness,
};

function KindBadge({ kind }: { kind: Notification["kind"] }) {
  const Icon = KIND_ICON[kind] ?? Bell;
  return (
    <span
      aria-hidden="true"
      className="absolute -end-1 -bottom-1 grid size-4.5 place-items-center rounded-full bg-surface text-ink-muted shadow-xs ring-1 ring-line [&_svg]:size-2.5"
    >
      <Icon strokeWidth={2.5} />
    </span>
  );
}

interface NotificationItemProps {
  notification: Notification;
  onOpen: (notification: Notification) => void;
}

/** One notification: who, what happened to you, the comment's words, when; a dot while unread. */
export function NotificationItem({ notification, onOpen }: NotificationItemProps) {
  const t = useT();
  const { locale } = useLocale();
  const unread = notification.read_at === null;
  const sentence = notificationSentence(notification, t, locale);
  const excerpt = notificationExcerpt(notification);
  const detail = notificationDetail(notification);
  const actor = notification.actor?.name ?? t("activity.someone");

  return (
    <li>
      <button
        type="button"
        onClick={() => {
          onOpen(notification);
        }}
        className={cn(
          "relative flex w-full gap-3 px-4 py-3.5 text-start transition-colors hover:bg-surface-hover focus-visible:bg-surface-hover focus-visible:outline-offset-[-2px]",
          // Unread: a sage wash and a bar on the start edge, so new items stand out at a glance.
          unread &&
            "bg-brand-soft/40 before:absolute before:inset-y-3 before:start-0 before:w-[3px] before:rounded-e-full before:bg-brand",
        )}
      >
        <span className="relative mt-0.5 h-fit shrink-0">
          <Avatar
            name={actor}
            src={notification.actor?.avatar_url}
            className="size-8 text-[12.5px]"
          />
          <KindBadge kind={notification.kind} />
        </span>
        <span className="min-w-0 flex-1">
          <RichSentence
            sentence={sentence}
            className="block text-[14px] leading-snug text-ink-muted"
          />
          {excerpt ? (
            <span className="mt-1 block border-s-2 border-line-strong ps-2.5">
              <span
                dir={textDirection(excerpt)}
                className="line-clamp-2 text-[13.5px] leading-relaxed text-ink user-text [unicode-bidi:isolate]"
              >
                {excerpt}
              </span>
            </span>
          ) : null}
          <span className="mt-1 flex min-w-0 items-center gap-1.5 text-[13px] text-ink-subtle">
            {detail ? <span className="user-text min-w-0 truncate">{detail}</span> : null}
            {detail ? <span aria-hidden="true">·</span> : null}
            <TimeAgo value={notification.created_at} />
          </span>
        </span>
        <span className="flex w-2 shrink-0 justify-center pt-1.5">
          {unread ? (
            <>
              <span aria-hidden="true" className="size-2 rounded-full bg-brand" />
              <span className="sr-only">{t("notifications.unread")}</span>
            </>
          ) : null}
        </span>
      </button>
    </li>
  );
}
